import { useMutation } from '@tanstack/react-query';
import Translatable from '../translatable_text/Translatable';
import api from '@/context/apiRequest';
import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { z } from 'zod';
import MsgComponent from '../msg_component/MsgComponent';
import { AxiosError } from 'axios';
import Dialog from '../custom-components/Dialog';
import { useSelector } from '@/store/Store';

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  title: z.string().min(3, 'Title must be at least 3 characters'),
  phone: z.string().regex(/^\+966[0-9]{9}$/, 'Phone must be in format +966501234567'),
  message: z.string().min(10, 'Message must be at least 10 characters')
});

type ContactFormValues = z.infer<typeof contactSchema>;

function Contact() {

  const rtl = useSelector(( state ) => state.customizer.activeDir === 'rtl');

  const [values, setValues] = useState<ContactFormValues>({ name: '', email: '', title: '', message: '', phone: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormValues, string>>>({});
  const [resErr, setResErr] = useState('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const mutation = useMutation({
    mutationFn: async (formData: object) => {
      await api().post(`/api/v1/guest/contact-messages`, formData)
    },
    onSuccess: (data) =>  {
      console.log("success: ", data);
      setValues({ name: '', email: '', title: '', message: '', phone: '' });
      setErrors({});
      setOpenDialog(true)
    },
    onError: (error) => {
      console.log(error)
      if(error instanceof AxiosError) {
        setResErr(error.response?.data?.message || error.response?.data?.error || 'Sumbission failed');
        console.log('Message error:', error);
      }
    }
  })

  const handleChange = (field: keyof ContactFormValues) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});


    const result = contactSchema.safeParse(values);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormValues, string>> = {};
      result.error.issues.forEach(issue => {
        const key = issue.path[0] as keyof ContactFormValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }
    
    mutation.mutate(result.data);
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setResErr('')
    }, 8000);
  
    // Cleanup function: Clear the timeout when the component unmounts or err changes
    return () => {
      clearTimeout(timer);
    };
    
  }, [resErr])

  
  return (
    <section className="contact section-padding sub-bg padded-container">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-12 gap-6 items-start">
          <div className="col-span-12 lg:col-span-5 valign">
            <div className="sec-head md-mb80">
              <h2 className="text-u ls1 d-rotate wow text-4xl">
                <span className="rotate-text">
                  <Translatable>
                    Your investment
                  </Translatable>
                  {' '}
                  <br />
                  <span className="fw-200">
                    <Translatable>
                      worth!
                    </Translatable>
                  </span>
                </span>
              </h2>
              <span style={{ color: 'gray' }} className="mt-20 mb-20">
                <Translatable>
                  If you would like to work with us or just want to get in touch, we’d love to hear from you!
                </Translatable>
              </span>
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6">
                  <div className="morinfo mt-30">
                    <h6 className="mb-15"><Translatable>Address</Translatable></h6>
                    <p>KSA, Al Shoqiyah, Mecca 25553</p>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6 centered-element-resp">
                  <div className="morinfo mt-30">
                    <h6 className="mb-15"><Translatable>Email</Translatable></h6>
                    <a href='mailto:co@begining.online'>co@begining.online</a>
                  </div>
                </div>
              </div>
              <div className="phone fz-30 fw-600 mt-30 underline main-color centered-element-resp">
                <a aria-label='phone link' href="tel:+966595951565">+966 59 595 1565</a>
              </div>
              <ul className="rest social-text flex mt-60 centered-element-resp">
       
                <li className="mr-30">
                  <a aria-label='link to gcad site' href="https://x.com/GCAD_SA"  className="hover-this" target='_blank'> 
                    <span className="hover-anim">Twitter</span>
                  </a>
                </li>
                <li className="mr-30">
                  <a href="https://www.linkedin.com/company/gcad-sa/posts/?feedView=all" aria-label='link button' target='_blank' rel="noreferrer" className="hover-this">
                    <span className="hover-anim">LinkedIn</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-8 valign">
            <div className="full-width">
              <div className="sec-head mb-50">
                <h3 className="text-u ls1">
                  <Translatable>Send a</Translatable> <span className="fw-200"><Translatable>message</Translatable></span>
                </h3>
              </div>
              <form id="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="messages"></div>

                <div className="controls grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="form-group mb-30">
                      <input
                        id="form_name"
                        type="text"
                        name="name"
                        placeholder={rtl? "الاسم" :"Name"}
                        value={values.name}
                        onChange={handleChange('name')}
                      />
                      {errors.name && <small style={{ color: 'red' }}>{errors.name}</small>}
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <input
                        id="form_email"
                        type="email"
                        name="email"
                        placeholder={rtl? "الايميل" :"Email"}
                        value={values.email}
                        onChange={handleChange('email')}
                      />
                      {errors.email && <small style={{ color: 'red' }}>{errors.email}</small>}
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <div className="form-group mb-30">
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder="+966501234567"
                        value={values.phone}
                        onChange={handleChange('phone')}
                      />
                      {errors.phone && <small style={{ color: 'red' }}>{errors.phone}</small>}
                    </div>
                  </div>

                  <div className="col-span-12">
                    <div className="form-group mb-30">
                      <input
                        id="form_title"
                        type="text"
                        name="title"
                        placeholder={rtl? "العنوان" :"Title"}
                        value={values.title}
                        onChange={handleChange('title')}
                      />
                      {errors.title && <small style={{ color: 'red' }}>{errors.title}</small>}
                    </div>
                  </div>

                  <div className="col-span-12">
                    <div className="form-group">
                      <textarea
                        id="form_message"
                        name="message"
                        placeholder={rtl? "أكتب رسالتك هنا" :"Write you message here"}
                        value={values.message}
                        onChange={handleChange('message')}
                      ></textarea>
                      {errors.message && <small style={{ color: 'red' }}>{errors.message}</small>}
                    </div>

                    <MsgComponent message={resErr} className="error-msg"/>

                    <div className="mt-30">
                      <button
                        type="submit"
                        className="butn butn-full butn-bord radius-30"
                        disabled={mutation.isPending}
                      >
                        <span className="text"><Translatable>{mutation.isPending ? 'Submitting...' : 'Lets Talk'}</Translatable></span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        style={{ padding: 20, width: 500, backgroundColor: 'white' }}
        open={openDialog}
        title={<Translatable>Thank you</Translatable>}
        description={
          <Translatable>
            Your Message is Sent Successfully!
          </Translatable>
        }
        onClose={() => setOpenDialog(false)}
      />

    </section>
  );
}

export default Contact;
